import React, { useState } from "react";
import {
  CheckCircle,
  Email,
  Language,
  LocationOn,
  Phone,
  YouTube,
} from "@mui/icons-material";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";

import BASE_URL from "../config/apiConfig";
import NewsletterSubscription from "../components/NewsLetter";
import FAQ from "../components/FAQ/FAQ";

import "./ContactPage.css";

export default function ContactPage() {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    description: "",
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  const validateEmail = (email) => {
    const re =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/;

    return re.test(String(email).toLowerCase());
  };

  const validateForm = () => {
    return (
      formData.name &&
      formData.email &&
      formData.phone &&
      formData.description &&
      validateEmail(formData.email) &&
      formData.phone.length === 10
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleContact = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      console.error("Form validation failed");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();

        console.log("Successful:", data);

        setDialogOpen(true);

        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          description: "",
        });
      } else {
        console.error("Contact us failed");
      }
    } catch (error) {
      console.error("Error during Contact us:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="farmology-contact-page">
      {/* =====================================================
          HERO
      ===================================================== */}
      <section className="contact-page-hero">
        <img
          src="/Images/Contact_us.webp"
          alt="Contact Farmology"
          className="contact-hero-image"
        />

        <div className="contact-hero-overlay"></div>

        <div className="contact-hero-content">
          <div className="contact-hero-eyebrow">LET'S WORK TOGETHER</div>

          <h1>
            Let’s grow
            <br />
            <span>something better.</span>
          </h1>

          <p>
            Have a question, need support, or want to explore how Farmology can
            help your farming journey? We’d love to hear from you.
          </p>

          <div className="contact-breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <span>Contact Us</span>
          </div>
        </div>

        <div className="contact-hero-number">04</div>

        <div className="contact-hero-bottom">
          <span>CONNECT WITH FARMOLOGY</span>
          <div></div>
          <span>WE'RE HERE TO HELP</span>
        </div>
      </section>

      {/* =====================================================
          CONTACT INTRO
      ===================================================== */}
      <section className="contact-intro-section">
        <div className="contact-container">
          <div className="contact-section-heading">
            <div>
              <span className="contact-eyebrow">
                <span></span>
                GET IN TOUCH
              </span>

              <h2>
                Tell us what
                <br />
                you need.
              </h2>
            </div>

            <div className="contact-heading-side">
              <span></span>

              <p>
                Whether you are looking for information, technical support,
                partnerships, or simply want to know more about Farmology, our
                team is ready to help.
              </p>
            </div>
          </div>

          {/* =================================================
              MAIN CONTACT GRID
          ================================================= */}
          <div className="contact-main-grid">
            {/* FORM */}
            <div className="contact-form-card">
              <div className="contact-card-top">
                <span>01 — MESSAGE</span>

                <div className="contact-card-number">01</div>
              </div>

              <h3>
                Please enter
                <br />
                your query here.
              </h3>

              <p className="contact-form-intro">
                Fill out the form and our team will get back to you as soon as
                possible.
              </p>

              <form onSubmit={handleContact}>
                <div className="contact-form-row">
                  {/* NAME */}
                  <div className="contact-field">
                    <label>First Name *</label>

                    <input
                      type="text"
                      placeholder="Your name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* EMAIL */}
                  <div className="contact-field">
                    <label>Email Address *</label>

                    <input
                      type="email"
                      placeholder="you@example.com"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />

                    {!validateEmail(formData.email) &&
                      formData.email.length > 0 && (
                        <small className="contact-field-error">
                          Invalid email format
                        </small>
                      )}
                  </div>
                </div>

                {/* PHONE */}
                <div className="contact-field">
                  <label>Phone Number *</label>

                  <input
                    type="tel"
                    placeholder="10 digit phone number"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (/^\d*$/.test(value) && value.length <= 10) {
                        handleInputChange(e);
                      }
                    }}
                    required
                  />

                  {formData.phone.length > 0 &&
                    formData.phone.length !== 10 && (
                      <small className="contact-field-error">
                        Phone number must be 10 digits
                      </small>
                    )}
                </div>

                {/* SUBJECT */}
                <div className="contact-field">
                  <label>Subject</label>

                  <input
                    type="text"
                    placeholder="How can we help?"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                  />
                </div>

                {/* MESSAGE */}
                <div className="contact-field">
                  <div className="contact-message-label">
                    <label>Message *</label>

                    <span>{formData.description.length}/4000</span>
                  </div>

                  <textarea
                    placeholder="Tell us more about your query..."
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={6}
                    maxLength={4000}
                    required
                  />
                </div>

                {/* BUTTON */}
                <button
                  type="submit"
                  className="contact-submit-button"
                  disabled={loading}
                >
                  <span>{loading ? "Sending..." : "Send Message"}</span>

                  <span className="contact-submit-circle">→</span>
                </button>
              </form>
            </div>

            {/* CONTACT DETAILS */}
            <aside className="contact-details-card">
              <div className="contact-details-top">
                <span>02 — CONTACT</span>

                <div className="contact-details-icon">
                  <LocationOn />
                </div>
              </div>

              <h3>
                We’re always
                <br />
                happy to hear
                <br />
                from you.
              </h3>

              {/* ADDRESS */}
              <a
                href="https://maps.app.goo.gl/sTqQZCAiD82h3AhB8"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-detail-item"
              >
                <div className="contact-detail-icon">
                  <LocationOn />
                </div>

                <div>
                  <span>VISIT US</span>

                  <strong>Surobhi Agro Industries Private Limited</strong>

                  <p>
                    East India Coworking - Eden, 2nd Floor, Hanuman Estates, 19,
                    RN Mukherjee Rd, Esplanade, Lal Bazar, Kolkata, West Bengal
                    700001
                  </p>
                </div>
              </a>

              {/* PHONE */}
              <a href="tel:+917044245566" className="contact-detail-item">
                <div className="contact-detail-icon">
                  <Phone />
                </div>

                <div>
                  <span>CALL US</span>

                  <strong>+91 7044245566</strong>
                </div>
              </a>

              {/* EMAIL */}
              <a
                href="mailto:info@farmologyindia.com"
                className="contact-detail-item"
              >
                <div className="contact-detail-icon">
                  <Email />
                </div>

                <div>
                  <span>EMAIL US</span>

                  <strong>info@farmologyindia.com</strong>
                </div>
              </a>

              {/* WEBSITE */}
              <a
                href="https://www.farmology.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-detail-item"
              >
                <div className="contact-detail-icon">
                  <Language />
                </div>

                <div>
                  <span>WEBSITE</span>

                  <strong>www.farmology.ai</strong>
                </div>
              </a>

              {/* SOCIALS */}
              <div className="contact-social-section">
                <span>FOLLOW FARMOLOGY</span>

                <div className="contact-socials">
                  <a
                    href="https://www.facebook.com/p/Farmology-100063893500354/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <Facebook size={18} />
                  </a>

                  <a
                    href="https://www.youtube.com/channel/UCgjha5OQ4B0pdntZGAncvaQ"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="YouTube"
                  >
                    <YouTube sx={{ fontSize: 19 }} />
                  </a>

                  <a
                    href="https://www.instagram.com/farmo_logy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} />
                  </a>

                  <a
                    href="https://in.linkedin.com/company/farmology-surobhi-agro"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={18} />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}
      <section className="contact-faq-section">
        <div className="contact-container">
          <div className="contact-section-heading">
            <div>
              <span className="contact-eyebrow">
                <span></span>
                NEED HELP?
              </span>

              <h2>
                Frequently
                <br />
                asked questions.
              </h2>
            </div>

            <div className="contact-heading-side">
              <span></span>

              <p>
                Find quick answers to some of the most common questions about
                Farmology and our solutions.
              </p>
            </div>
          </div>

          <div className="contact-faq-box">
            <FAQ />
          </div>
        </div>
      </section>

      {/* =====================================================
          NEWSLETTER
      ===================================================== */}
      <section className="contact-newsletter-section">
        <div className="contact-container">
          <NewsletterSubscription />
        </div>
      </section>

      {/* =====================================================
          SUCCESS DIALOG
      ===================================================== */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          className: "contact-success-dialog",
        }}
      >
        <DialogTitle>Message Sent</DialogTitle>

        <DialogContent>
          <div className="contact-success-content">
            <CheckCircle />

            <h3>Your message has been sent successfully!</h3>

            <p>We'll get back to you as soon as possible.</p>

            <button
              onClick={() => setDialogOpen(false)}
              className="contact-success-button"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
